import { useEffect, useRef, useState } from 'react';
import { api, fileSizeLabel, openSiteDocument, type SiteDocument } from '../lib/api';

/**
 * A hospital's documents (D98) — the "Site Overview" and its like.
 *
 * The site guide used to say "ask the office for the link", which means an email, which means
 * the physician has a copy that ages while the real one moves on. The office uploads it here
 * once and every physician going to that hospital reads the same file.
 */

/** One row, on either side of the platform. Opening goes through the API so the login rides with it. */
function DocumentRow({ siteId, doc, onRemove }: { siteId: string; doc: SiteDocument; onRemove?: () => void }) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const open = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await openSiteDocument(siteId, doc);
    } catch (e) {
      setProblem(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="sitedoc">
      <button type="button" className="linklike docname" disabled={busy} onClick={() => void open()}>
        {doc.name}
      </button>
      <span className="muted small">
        {doc.contentType === 'application/pdf' ? 'PDF' : 'Word'} · {fileSizeLabel(doc.sizeBytes)}
        {doc.inline ? '' : ' · downloads'}
      </span>
      {onRemove && (
        <button type="button" className="linklike small danger noprint" onClick={onRemove}>
          Remove
        </button>
      )}
      {problem && <span className="error small">{problem}</span>}
    </li>
  );
}

/** What a physician sees on their trip page: read only, and only while they still go there. */
export function SiteDocumentList({ siteId, documents }: { siteId: string; documents: SiteDocument[] }) {
  if (!documents.length) return null;
  return (
    <div className="sitedocs">
      <h4>Hospital documents</h4>
      <ul>
        {documents.map((d) => (
          <DocumentRow key={d.id} siteId={siteId} doc={d} />
        ))}
      </ul>
    </div>
  );
}

/** The coordinator's side: add a document, take one away. */
export function SiteDocumentManager({ siteId, siteName }: { siteId: string; siteName: string }) {
  const [docs, setDocs] = useState<SiteDocument[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);

  const load = () => api.siteDocuments(siteId).then(setDocs).catch(() => setDocs([]));
  useEffect(() => { void load(); }, [siteId]);

  const add = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    setProblem(null);
    setNote(null);
    try {
      for (const file of Array.from(files)) {
        // Sent as base64 like a photograph, so the API needs no upload middleware and no second
        // place for a file to live. The API refuses anything the BYTES do not support.
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result));
          reader.onerror = () => reject(new Error(`${file.name} could not be read`));
          reader.readAsDataURL(file);
        });
        const name = file.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim().slice(0, 200) || null;
        await api.addSiteDocument(siteId, { file: base64, name });
      }
      setNote(`Added to ${siteName}. Physicians see it on their trip page for this hospital.`);
      await load();
    } catch (e) {
      setProblem(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      if (picker.current) picker.current.value = '';
    }
  };

  const remove = async (d: SiteDocument) => {
    setBusy(true);
    setProblem(null);
    try {
      await api.removeSiteDocument(siteId, d.id);
      await load();
    } catch (e) {
      setProblem(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sitedocs">
      <h4>Hospital documents</h4>
      <p className="muted small">
        The site overview and anything like it. A physician sees these on their trip page for this hospital, from the
        moment they are scheduled until their last stint here is over — so there is no link to email and nothing to
        take away afterwards. PDF or Word, up to 10MB.
      </p>
      {docs === null && <p className="muted small">Loading…</p>}
      {docs?.length === 0 && <p className="muted small">No documents yet.</p>}
      {docs && docs.length > 0 && (
        <ul>
          {docs.map((d) => (
            <DocumentRow key={d.id} siteId={siteId} doc={d} onRemove={() => void remove(d)} />
          ))}
        </ul>
      )}
      <input ref={picker} type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" multiple disabled={busy} onChange={(e) => void add(e.target.files)} />
      {busy && <p className="muted small">Working…</p>}
      {note && <p className="small">{note}</p>}
      {problem && <p className="error small">{problem}</p>}
    </div>
  );
}
